export type Volume = {
  number: string
  title: string
  tagline: string
  intro: string[]
  listIntro: string
  items: string[]
  ordered?: boolean
  formula?: string[]
  outro: string[]
}

export const volumes: Volume[] = [
  {
    number: 'I',
    title: 'The TCM Identification Blueprint™',
    tagline: "Know What You're Working With Before Choosing Your Next Step",
    intro: [
      "Before investigating a problem, you need to establish what module and vehicle you're dealing with.",
      'This volume helps you organize the information that matters before proceeding.',
    ],
    listIntro: "Inside, you'll find a structured approach to documenting:",
    items: [
      'TCM manufacturer and part number.',
      'Hardware and software references.',
      'Vehicle make, model, year, and configuration.',
      'Transmission identification.',
      'Available module identification information.',
      'Relevant connectors and documentation.',
      'Previous repairs or interventions.',
      'Missing information that needs verification.',
    ],
    outro: [
      "You'll create a structured TCM Information Profile to use throughout the diagnostic process.",
      'The goal is to reduce confusion at the beginning of a job and establish a consistent starting point.',
    ],
  },
  {
    number: 'II',
    title: 'The TCM Diagnostic Decision Map™',
    tagline: 'Stop Guessing What Information You Need Next',
    intro: [
      'This is the core of the system.',
      "Instead of jumping between unrelated possibilities, you'll use visual decision maps to organize your investigation.",
    ],
    listIntro: "You'll find structured pathways for:",
    items: [
      'Communication problems.',
      'Transmission-related fault codes.',
      'Reading and backup issues.',
      'Programming errors.',
      'Unexpected diagnostic results.',
      'Abnormal behavior following an intervention.',
    ],
    outro: [
      'Each pathway helps you organize the situation, identify missing information, record relevant checks, and determine what should be investigated next.',
      "The purpose isn't to claim that one symptom always has one cause. It's to give you a logical structure for investigating the possibilities.",
    ],
  },
  {
    number: 'III',
    title: 'The TCM Tool Selection Matrix™',
    tagline: 'Evaluate the Requirements Before Choosing a Tool',
    intro: [
      "Having multiple programmers and diagnostic devices doesn't automatically mean you know which one fits a particular job.",
      "In this volume, you'll learn to organize your tool-selection process around the actual requirements of the task.",
    ],
    listIntro: "You'll work through:",
    items: [
      'The objective of the job.',
      'TCM identification.',
      'Required access method.',
      'Available tool categories.',
      'Declared tool capabilities.',
      'Supported protocols.',
      'Connection requirements.',
      'Compatibility verification.',
      'Known limitations.',
      'Documentation required before proceeding.',
    ],
    outro: [
      "You'll also receive comparison templates to help you evaluate tools using documented requirements rather than assumptions.",
      "The goal isn't to recommend one universal tool. It's to help you make better-informed decisions about the tools you already own and the ones you may need.",
    ],
  },
  {
    number: 'IV',
    title: 'The Safe TCM Workflow™',
    tagline: 'Follow a Consistent Process From Intake to Verification',
    intro: [
      'Every job benefits from a structured workflow.',
      'This volume organizes the main stages of a TCM job into a repeatable process:',
    ],
    listIntro: '',
    ordered: true,
    items: [
      'Identify the module.',
      'Document the initial condition.',
      'Collect the relevant information.',
      'Review the applicable technical documentation.',
      'Prepare the job.',
      'Record backups and files, when applicable.',
      'Perform the authorized procedure.',
      'Verify the results.',
      'Document the outcome.',
    ],
    outro: [
      "You'll receive professional checklists designed to help you keep track of the work and avoid overlooking important preparation or verification steps.",
      'The goal is a more organized process, not a substitute for manufacturer-specific procedures or hands-on technical competence.',
    ],
  },
  {
    number: 'V',
    title: 'The TCM Troubleshooting Playbook™',
    tagline: "Know How to Organize the Investigation When Things Don't Go as Expected",
    intro: ['Not every TCM job follows a straightforward path.'],
    listIntro: 'You may encounter:',
    items: [
      'Communication failures.',
      'Reading problems.',
      'Unstable connections.',
      'Unexpected file results.',
      'Programming interruptions.',
      'Unusual behavior after an intervention.',
    ],
    formula: ['Situation', 'Information Needed', 'Checks', 'Findings', 'Decision', 'Next Step'],
    outro: [
      'When something goes wrong, jumping immediately to the conclusion that the TCM is defective can lead you in the wrong direction.',
      'This volume gives you a structured way to document what happened, review the available evidence, identify information gaps, and determine the next appropriate investigation step.',
      "You'll also learn when to stop, consult the applicable technical documentation, or seek additional specialist support.",
    ],
  },
  {
    number: 'VI',
    title: 'The TCM Workshop Documentation System™',
    tagline: 'Turn Your Diagnostic Process Into a Repeatable Workshop System',
    intro: [
      "A professional workflow doesn't end when you've identified a possible cause.",
      'You also need to document the job, organize the relevant files, and record what was actually verified.',
    ],
    listIntro: 'This volume helps you organize:',
    items: [
      'TCM intake records.',
      'Vehicle and module information.',
      'Diagnostic observations.',
      'Tool and procedure records.',
      'Original files and backups, where applicable.',
      'Diagnostic notes.',
      'Intervention history.',
      'Verification results.',
      'Final reports.',
      'Workshop job records.',
    ],
    outro: [
      "You'll receive practical templates to help you keep your documentation consistent across different jobs.",
      "It's about establishing a repeatable process you can use throughout your workshop.",
    ],
  },
]
